import { ArgumentException } from '../../../exceptions/argument-exception';
import { Currency } from '../../../models/serializable/currency';
import { GenericOrder } from '../../../models/serializable/orders/generic-order';
import { GenericOrderTicketEntry } from '../../../models/serializable/orders/generic-order-entries/generic-order-ticket-entry';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../../../models/serializable/orders/generic-order-entry';
import { Price } from '../../../models/serializable/price';
import { DiscountHelper } from '../../discount-helper';
import { TicketPricing } from '../../ticket-pricing.js';
import { FeeHelper } from '../../fee-helper';
import { GenericOrderDiscountBreakdownEntry } from '../discount-breakdown';
import { GenericOrderFeeBreakdownEntry } from '../fee-breakdown';
import { TicketOrderDiscountMode } from '../ticket-order-discount-mode';
import { IGenericOrderPaymentHelperAdapter } from './generic-order-payment-helper-adapter';

export class TicketEntryPaymentHelperAdapter extends IGenericOrderPaymentHelperAdapter {
  public readonly orderEntryTypeId: string = GenericOrderTicketEntry.TYPE_ID;

  static verifyType(dm: IGenericOrderPaymentHelperAdapter): boolean {
    return dm instanceof TicketEntryPaymentHelperAdapter;
  }

  static assertType(dm: IGenericOrderPaymentHelperAdapter): dm is TicketEntryPaymentHelperAdapter {
    return dm instanceof TicketEntryPaymentHelperAdapter;
  }

  getCurrency(order: GenericOrder, orderEntry: GenericOrderEntry): Currency | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this._getCurrency(orderEntry);
  }

  calculateDiscountBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      orderEntryIndex: number
  ): GenericOrderDiscountBreakdownEntry[] | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    const pricing = TicketPricing.calculate(orderEntry);
    if(pricing == null) return null;
    const entries: GenericOrderDiscountBreakdownEntry[] = [];

    for(let ti = 0; ti < pricing.tickets.length; ti++) {
      const ticketPricing = pricing.tickets[ti];
      if(DiscountHelper.hasDiscountValue(ticketPricing.ticket.discount)) {
        entries.push({ price: ticketPricing.ticketDiscount, host: { type: 'ticket', orderEntryIndex, itemIndex: ti } });
      }
    }

    if(DiscountHelper.hasDiscountValue(orderEntry.discount)) {
      entries.push({ price: pricing.groupDiscountTotal, host: { type: 'orderEntry', orderEntryIndex } });
    }

    return entries;
  }

  calculateFeeBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      orderEntryIndex: number
  ): GenericOrderFeeBreakdownEntry[] | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    const entries: GenericOrderFeeBreakdownEntry[] = [];

    for(let ti = 0; ti < orderEntry.tickets.length; ti++) {
      const ticket = orderEntry.tickets[ti];
      const result = FeeHelper.calculateFeeValueForEventTicket(ticket);

      if(result != null) {
        entries.push({ ...result, host: { type: 'ticket', orderEntryIndex, itemIndex: ti } });
      }
    }

    return entries;
  }

  calculateCheckoutPrice(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: true,
      discountMode: 'Everything',
    });
  }

  calculateIncome(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: false,
      discountMode: 'Everything',
    });
  }

  calculateIncomeWithoutDiscount(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: false,
      discountMode: 'None',
    });
  }

  calculateDiscount(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }
    if(orderEntry.discount == null) {
      return null;
    }

    return TicketPricing.calculate(orderEntry)?.groupDiscountTotal ?? null;
  }

  calculateCheckoutPriceForDiscountProvider(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: false,
      discountMode: 'TicketsOnly',
    });
  }

  canCreateRefund(order: GenericOrder, orderEntry: GenericOrderEntry): boolean | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    if(orderEntry.status !== GenericOrderEntryStatus.Refunded) {
      return false;
    }

    return orderEntry.refunds.length <= 0;
  }

  calculateMaxSimulatedRefund(order: GenericOrder, orderEntry: GenericOrderEntry, options: { includeFee: boolean }): Price | null {
    if(!GenericOrderTicketEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    let orderPrice = this.calculateIncome(order, orderEntry);
    if(orderPrice == null) {
      return null;
    }

    if(orderEntry.applicationFee != null && options.includeFee) {
      orderPrice = orderPrice.add(orderEntry.applicationFee);
    }

    return orderPrice;
  }

  private calculatePrice({ orderEntry, applyFee, discountMode }: {
      orderEntry: GenericOrderTicketEntry,
      applyFee: boolean,
      discountMode: TicketOrderDiscountMode,
    }): Price | null {
    const currency = this._getCurrency(orderEntry);
    if(currency == null) {
      return null;
    }

    const ticketPricing = TicketPricing.calculate(orderEntry, discountMode);
    if(ticketPricing == null) return null;
    let orderEntryPrice = ticketPricing.total;

    if(applyFee) {
      if(orderEntry.applicationFee != null) {
        orderEntryPrice = orderEntryPrice.add(orderEntry.applicationFee);
      }
    }

    return orderEntryPrice;
  }

  _getCurrency(orderEntry: GenericOrderTicketEntry): Currency | null {
    if(orderEntry.tickets.length <= 0) {
      return null;
    }

    return orderEntry.tickets[0].template.price.currency;
  }
}
